//Fetches all matching audit pages, optionally fills in reason from detail calls, 
// builds filter-based filenames, builds CSV, and triggers a text-file download.
import {
  getAuditLogDetail,
  getAuditLogsPagedByScope,
  type AuditLogsScope,
  type AuditLog,
  type GetAuditLogsParams,
} from '../api/audits';

const EXPORT_PAGE_SIZE = 200;

function formatTimestampForExport(raw?: string | null): string {
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function escapeCsvCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function exportFilenameStamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function sanitizeFilenamePart(raw: string): string {
  const s = raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return s || 'x';
}

export function buildAuditExportFilename(
  filters: Pick<GetAuditLogsParams, 'action' | 'startDate' | 'endDate' | 'externalClientId' | 'createdBy'>,
  ext: 'csv' | 'pdf'
): string {
  const segs: string[] = ['audit-logs'];
  const hasFilter = Boolean(
    filters.action?.trim() ||
      filters.startDate?.trim() || filters.endDate?.trim() ||
      filters.externalClientId?.trim() ||
      filters.createdBy?.trim()
  );
  if (!hasFilter) segs.push('all');
  if (filters.action?.trim()) segs.push(`action-${sanitizeFilenamePart(filters.action.trim())}`);
  if (filters.startDate?.trim()) segs.push(`from-${sanitizeFilenamePart(filters.startDate.trim())}`);
  if (filters.endDate?.trim()) segs.push(`to-${sanitizeFilenamePart(filters.endDate.trim())}`);
  if (filters.externalClientId?.trim()) {
    segs.push(`client-${sanitizeFilenamePart(filters.externalClientId.trim())}`);
  }
  if (filters.createdBy?.trim()) segs.push(`agent-${sanitizeFilenamePart(filters.createdBy.trim())}`);
  segs.push(exportFilenameStamp());
  return `${segs.join('_')}.${ext}`;
}

const REASON_ENRICH_CONCURRENCY = 12;

/** Fills `reason` from the detail endpoint when the list payload did not include it. */
export async function enrichAuditLogsWithReasons(logs: AuditLog[]): Promise<AuditLog[]> {
  const needIdx = logs
    .map((log, i) => (!log.reason?.trim() ? i : -1))
    .filter((i) => i >= 0);
  if (needIdx.length === 0) return logs;

  const next = logs.map((l) => ({ ...l }));

  for (let i = 0; i < needIdx.length; i += REASON_ENRICH_CONCURRENCY) {
    const slice = needIdx.slice(i, i + REASON_ENRICH_CONCURRENCY);
    await Promise.all(
      slice.map(async (idx) => {
        const log = next[idx];
        const res = await getAuditLogDetail(log.id);
        if (res.success && res.data) {
          next[idx] = { ...log, reason: res.data.reason ?? '' };
        }
      })
    );
  }

  return next;
}

export async function fetchAllAuditLogsForExport(
  filters: Pick<GetAuditLogsParams, 'action' | 'startDate' | 'endDate' | 'externalClientId' | 'createdBy' | 'entityType'>,
  scope: AuditLogsScope = 'all'
): Promise<{ ok: true; logs: AuditLog[] } | { ok: false; message: string }> {
  const logs: AuditLog[] = [];
  let page = 0;
  let totalPages = 1;

  while (page < totalPages) {
    const res = await getAuditLogsPagedByScope(scope, {
      ...filters,
      page,
      size: EXPORT_PAGE_SIZE,
    });

    if (!res.success || !res.data) {
      return { ok: false, message: res.message ?? 'Failed to fetch audit logs for export.' };
    }

    logs.push(...(res.data.logs ?? []));
    totalPages = res.data.totalPages ?? 1;
    page += 1;
  }

  return { ok: true, logs };
}

export function buildAuditLogsCsv(logs: AuditLog[]): string {
  const headers = [
    'Timestamp',
    'FXBO ID',
    'Client First Name',
    'Client Last Name',
    'Entity Type',
    'Reason',
    'Action',
    'Agent',
  ];

  const rows = logs.map((log) => [
    formatTimestampForExport(log.createdOn),
    log.externalClientId ?? '',
    log.clientFirstName ?? '',
    log.clientLastName ?? '',
    log.entityType ?? '',
    log.reason?.trim() ?? '',
    log.action ?? '',
    log.createdBy?.trim() ?? '',
  ]);

  const lines = [
    headers.map(escapeCsvCell).join(','),
    ...rows.map((r) => r.map(escapeCsvCell).join(',')),
  ];
  return `\uFEFF${lines.join('\r\n')}`;
}

export function downloadTextFile(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
